import { useState } from "react";
import { Button, Tag, Tooltip, Title, Icon, Stack } from "@introist/react-foundation/v2";
import styled from "styled-components";
import {
  PerformanceReviewEmployeeRow,
  PerformanceReviewScenarioType,
  sortSignalsByPriority,
  formatPrimarySignal
} from "../hooks/usePerformanceReviewData";
import { EmployeeMetricCard } from "../../wellbeing/components/EmployeeMetricCard";

const TableContainer = styled.div`
  border: 1px solid var(--palette-border-default);
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 2fr 3fr 120px 150px 180px;
  background: var(--palette-background-surface);
  border-bottom: 2px solid var(--palette-border-default);
  padding: var(--spacing-small) var(--spacing-medium);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--palette-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div<{ $severity?: "medium" | "high"; $expanded?: boolean }>`
  display: grid;
  grid-template-columns: 40px 2fr 3fr 120px 150px 180px;
  align-items: center;
  padding: var(--spacing-medium);
  border-bottom: 1px solid var(--palette-border-default);
  border-left: 4px solid ${({ $severity }) =>
    $severity === "high" ? "var(--palette-danger-default)" : "var(--palette-warning-default)"};
  cursor: pointer;
  transition: background 0.15s ease;
  background: ${({ $expanded }) => ($expanded ? "var(--palette-background-surface)" : "white")};

  &:hover {
    background: var(--palette-background-surface);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ExpandedContent = styled.div`
  grid-column: 1 / -1;
  padding: var(--spacing-large);
  background: var(--palette-background-default);
  border-top: 1px solid var(--palette-border-default);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--spacing-medium);
  margin-bottom: var(--spacing-large);
`;

const EmployeeName = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--palette-primary-light);
  color: var(--palette-primary-default);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
`;

const EllipsisText = styled(Title)`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SignalChip = styled(Tag)`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
`;

const SeverityTag = styled(Tag)<{ $severity: "medium" | "high" }>`
  min-width: 68px;
  justify-content: center;
  color: ${({ $severity }) =>
    $severity === "high" ? "var(--palette-danger-default)" : "var(--palette-warning-default)"};
`;

const ExpandIconWrapper = styled.div<{ $expanded: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "90deg" : "0deg")});
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionTitle = styled(Title)`
  margin-bottom: var(--spacing-small);
  color: var(--palette-text-secondary);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const scenarioTypeTitles: Record<PerformanceReviewScenarioType, string> = {
  NO_REVIEW_12M: "Overdue Review",
  NEW_MANAGER_NO_REVIEW: "New Manager — No Review",
  LONG_TENURE_NO_REVIEW: "Long Tenure — Never Reviewed",
  ROLE_CHANGE_REQUESTED: "Role Change Requested",
  FIRST_REVIEW_UPCOMING: "Probation Review Due"
};

type PerformanceReviewTableProps = {
  rows: PerformanceReviewEmployeeRow[];
  selectedWorkflowId?: string;
  onLaunchWorkflow: (employeeId: string) => Promise<void>;
  launchingEmployeeId: string | null;
};

export const PerformanceReviewTable = ({
  rows,
  selectedWorkflowId,
  onLaunchWorkflow,
  launchingEmployeeId
}: PerformanceReviewTableProps) => {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  return (
    <TableContainer>
      <TableHeader>
        <div></div>
        <Title>Employee</Title>
        <Title>Primary Signal</Title>
        <Title>Severity</Title>
        <Title>Signals</Title>
        <Title>Actions</Title>
      </TableHeader>

      {rows.map(row => {
        const orderedSignals = sortSignalsByPriority(row.scenarios);
        const signalCount = orderedSignals.length;
        const signalsTooltip = orderedSignals.map(s => scenarioTypeTitles[s.type]).join(", ");
        const signalSummary =
          signalCount === 1 ? scenarioTypeTitles[orderedSignals[0].type] : `${signalCount} signals`;
        const isExpanded = expandedRowId === row.employee.id;

        return (
          <div key={row.employee.id}>
            <TableRow
              $severity={row.topSeverity}
              $expanded={isExpanded}
              onClick={() => setExpandedRowId(isExpanded ? null : row.employee.id)}
            >
              <div style={{ display: "flex", justifyContent: "center" }}>
                <ExpandIconWrapper $expanded={isExpanded}>
                  <Icon name="chevronRight" style={{ fontSize: 16 }} />
                </ExpandIconWrapper>
              </div>

              <EmployeeName>
                <Avatar>{getInitials(row.employee.name ?? "Unknown")}</Avatar>
                <Title>{row.employee.name ?? "Unknown"}</Title>
              </EmployeeName>

              <Tooltip tooltip={formatPrimarySignal(orderedSignals[0])}>
                <EllipsisText>{formatPrimarySignal(orderedSignals[0])}</EllipsisText>
              </Tooltip>

              <SeverityTag
                $severity={row.topSeverity}
                colorVariant={row.topSeverity === "high" ? "danger" : "warning"}
              >
                {row.topSeverity === "high" ? "High" : "Medium"}
              </SeverityTag>

              <Tooltip tooltip={signalsTooltip}>
                <SignalChip colorVariant="surface">{signalSummary}</SignalChip>
              </Tooltip>

              <div onClick={e => e.stopPropagation()}>
                <Button
                  size="small"
                  onClickWithLoading={() =>
                    row.employee.id ? onLaunchWorkflow(row.employee.id) : Promise.resolve()
                  }
                  disabled={
                    !selectedWorkflowId ||
                    !row.employee.id ||
                    launchingEmployeeId === row.employee.id
                  }
                >
                  Launch workflow
                </Button>
              </div>
            </TableRow>

            {isExpanded && (
              <ExpandedContent>
                <Stack vertical gap="large">
                  <div>
                    <SectionTitle>Review History</SectionTitle>
                    <MetricsGrid>
                      <EmployeeMetricCard
                        label="Days Since Last Review"
                        value={
                          row.metrics.daysSinceLastReview === null
                            ? "Never"
                            : `${row.metrics.daysSinceLastReview} days`
                        }
                        icon="calendar"
                        status={
                          row.metrics.daysSinceLastReview === null
                            ? "danger"
                            : row.metrics.daysSinceLastReview > 545
                            ? "danger"
                            : row.metrics.daysSinceLastReview > 365
                            ? "warning"
                            : "good"
                        }
                      />
                      <EmployeeMetricCard
                        label="Total Reviews Held"
                        value={row.metrics.totalReviewsHeld}
                        icon="checkSquare"
                        status={
                          row.metrics.totalReviewsHeld === 0
                            ? "danger"
                            : row.metrics.totalReviewsHeld === 1
                            ? "warning"
                            : "good"
                        }
                      />
                      <EmployeeMetricCard
                        label="Days Since Start"
                        value={`${row.metrics.daysSinceStart} days`}
                        icon="clock"
                        status="good"
                      />
                    </MetricsGrid>
                  </div>

                  <div>
                    <SectionTitle>Manager & Role</SectionTitle>
                    <MetricsGrid>
                      <EmployeeMetricCard
                        label="Manager Changed"
                        value={
                          row.metrics.managerChangedDaysAgo === null
                            ? "No recent change"
                            : `${row.metrics.managerChangedDaysAgo} days ago`
                        }
                        icon="persons"
                        status={
                          row.metrics.managerChangedDaysAgo !== null &&
                          !row.metrics.reviewAfterManagerChange
                            ? "danger"
                            : "good"
                        }
                      />
                      <EmployeeMetricCard
                        label="Review After Manager Change"
                        value={
                          row.metrics.managerChangedDaysAgo === null
                            ? "N/A"
                            : row.metrics.reviewAfterManagerChange
                            ? "Yes"
                            : "No"
                        }
                        icon="checkCircle"
                        status={
                          row.metrics.managerChangedDaysAgo === null
                            ? "good"
                            : row.metrics.reviewAfterManagerChange
                            ? "good"
                            : "danger"
                        }
                      />
                      <EmployeeMetricCard
                        label="Role Change Requested"
                        value={row.metrics.wantsRoleChange ? "Yes" : "No"}
                        icon="arrowRight"
                        status={row.metrics.wantsRoleChange ? "danger" : "good"}
                      />
                    </MetricsGrid>
                  </div>
                </Stack>
              </ExpandedContent>
            )}
          </div>
        );
      })}
    </TableContainer>
  );
};
